import { useCallback, useState } from 'react';
import { wantsCompactSidebar } from './services';

export type SidebarMode = 'expanded' | 'collapsed';

const KEY = 'lifehub.sidebar';

// Simple préférence d'affichage : jamais de donnée sensible dans le stockage local (comme `theme.ts`).
function readStored(): SidebarMode {
  try {
    return window.localStorage.getItem(KEY) === 'collapsed' ? 'collapsed' : 'expanded';
  } catch {
    return 'expanded';
  }
}

function writeStored(mode: SidebarMode): void {
  try {
    window.localStorage.setItem(KEY, mode);
  } catch {
    // stockage indisponible (navigation privée) : le choix reste valable pour la session
  }
}

/**
 * État de la sidebar globale. `manual` est la préférence de l'utilisateur, mémorisée. Sur l'écran d'un service
 * qui a sa propre barre latérale (`compactSidebar`), la sidebar se réduit seule ; un clic pour la déplier ne vaut
 * que pour la visite en cours (`override`, attaché à l'adresse) et ne touche pas à la préférence.
 */
export function useSidebarState(pathname: string): { mode: SidebarMode; toggle: () => void } {
  const [manual, setManual] = useState<SidebarMode>(readStored);
  const [override, setOverride] = useState<{ pathname: string; mode: SidebarMode } | null>(null);
  const compact = wantsCompactSidebar(pathname);

  // Changer d'écran annule le choix fait pour la visite précédente.
  if (override && override.pathname !== pathname) setOverride(null);

  const mode: SidebarMode = override?.pathname === pathname ? override.mode : compact ? 'collapsed' : manual;

  const toggle = useCallback(() => {
    const next: SidebarMode = mode === 'expanded' ? 'collapsed' : 'expanded';
    if (compact) {
      setOverride({ pathname, mode: next });
    } else {
      setManual(next);
      writeStored(next);
    }
  }, [mode, compact, pathname]);

  return { mode, toggle };
}
